// Product analytics for app.signwith.co (GA4 + Microsoft Clarity).
//
// Every call is a no-op when gtag / Clarity are missing (ad blockers, test
// env, no GOOGLE_ANALYTICS_ID) and never throws. Params that could carry
// personal data are dropped. Shared event contract:
// signwith-landing/docs/analytics-tracking-plan.md

export const CLARITY_EVENTS = new Set(['sign_up', 'first_document_sent', 'purchase'])

// Link and file keys are blocked too: download links carry document names and
// signed blob ids, and our own events never need a URL.
const BLOCKED_KEYS = new Set([
  'email', 'name', 'first_name', 'last_name', 'full_name', 'title', 'document_title',
  'template_name', 'document_name', 'file_name', 'filename', 'slug', 'token', 'password', 'phone',
  'link_url', 'link_text', 'link_domain', 'link_id', 'link_classes', 'file_extension', 'url', 'href', 'page'
])
const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/
// "%40" and double-encoded "%2540" are "@".
const ENCODED_AT = /%(?:25)*40/gi
const URL_PATTERN = /^\s*(?:[a-z][a-z0-9+.-]*:)?\/\//i
const QUERY_OR_HASH = /[?#].*$/

const isEmailLike = (value) => EMAIL_PATTERN.test(value) || EMAIL_PATTERN.test(value.replace(ENCODED_AT, '@'))
const SEEN_KEY = 'sw_analytics_seen'
const MAX_SEEN = 100

const cleanValue = (value) => {
  if (value === null || value === undefined || value === '') return undefined
  if (typeof value === 'string') return isEmailLike(value) || URL_PATTERN.test(value) ? undefined : value.slice(0, 100)
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (typeof value === 'boolean') return value
  if (Array.isArray(value)) return value.map(cleanValue).filter((item) => item !== undefined)
  if (typeof value === 'object') return cleanParams(value)
  return undefined
}

export function cleanParams (params) {
  const result = {}
  if (!params || typeof params !== 'object') return result

  Object.keys(params).forEach((key) => {
    if (BLOCKED_KEYS.has(key)) return
    const value = cleanValue(params[key])
    if (value !== undefined) result[key] = value
  })

  return result
}

export function createAnalytics (env = {}) {
  const win = () => env.window || (typeof window !== 'undefined' ? window : undefined)
  const doc = () => env.document || (typeof document !== 'undefined' ? document : undefined)
  const state = { initialized: false, userId: null, lastLocation: null, seen: new Set() }

  const gtag = (...args) => {
    const fn = win()?.gtag
    if (typeof fn !== 'function') return false
    fn(...args)
    return true
  }

  const clarity = (...args) => {
    const fn = win()?.clarity
    if (typeof fn !== 'function') return false
    fn(...args)
    return true
  }

  const safe = (fn, fallback) => {
    try {
      return fn()
    } catch (e) {
      return fallback
    }
  }

  const storage = () => safe(() => win()?.sessionStorage, undefined)

  const loadSeen = () => safe(() => JSON.parse(storage()?.getItem(SEEN_KEY) || '[]'), [])

  const hasSeen = (key) => state.seen.has(key) || loadSeen().includes(key)

  const markSeen = (key) => {
    state.seen.add(key)
    safe(() => {
      const seen = loadSeen().filter((item) => item !== key)
      seen.push(key)
      storage()?.setItem(SEEN_KEY, JSON.stringify(seen.slice(-MAX_SEEN)))
    })
  }

  const track = (name, params = {}) => safe(() => {
    if (!name) return false
    const sent = gtag('event', name, cleanParams(params))
    if (CLARITY_EVENTS.has(name)) clarity('event', name)
    return sent
  }, false)

  // Fires an event and resolves once GA has sent it (or after +timeout+ ms),
  // so a following full-page redirect does not drop it.
  const trackBeforeNavigate = (name, params = {}, timeout = 600) => new Promise((resolve) => {
    let done = false
    const finish = () => {
      if (done) return
      done = true
      resolve()
    }

    try {
      const sent = gtag('event', name, { ...cleanParams(params), event_callback: finish, event_timeout: timeout })
      if (CLARITY_EVENTS.has(name)) clarity('event', name)
      if (!sent) return finish()
      setTimeout(finish, timeout + 50)
    } catch (e) {
      finish()
    }
  })

  const setTag = (key, value) => safe(() => {
    if (!key || value === undefined || value === null || value === '') return false
    return clarity('set', key, String(value))
  }, false)

  const readContext = () => safe(() => {
    const meta = doc()?.querySelector('meta[name="sw-analytics"]')
    return meta ? JSON.parse(meta.getAttribute('content') || '{}') || {} : {}
  }, {})

  const identify = (ctx) => safe(() => {
    if (!ctx.user_id) return
    clarity('identify', String(ctx.user_id))
    setTag('plan_type', ctx.plan_type || 'free')
  })

  const consumeEvents = (root = doc()) => safe(() => {
    if (!root?.querySelectorAll) return 0
    let fired = 0

    root.querySelectorAll('script[data-sw-analytics-events]').forEach((element) => {
      const events = safe(() => JSON.parse(element.textContent || '[]'), [])
      element.remove()

      ;(Array.isArray(events) ? events : []).forEach((event) => {
        if (!event || !event.name) return
        const keys = [event.id && `id:${event.id}`, event.dedupe_key && `key:${event.dedupe_key}`].filter(Boolean)
        if (keys.some(hasSeen)) return
        keys.forEach(markSeen)

        Object.entries(event.clarity_tags || {}).forEach(([key, value]) => setTag(key, value))
        track(event.name, event.params || {})
        fired += 1
      })
    })

    return fired
  }, 0)

  // Runs on every full load and Turbo visit. The head gtag snippet already
  // sent the first page_view with the sanitised title; later visits are
  // sent from here because Turbo does not re-run head scripts.
  const handlePageLoad = () => safe(() => {
    const ctx = readContext()
    const boot = win()?.swAnalyticsBoot

    if (!state.initialized) {
      state.initialized = true
      state.userId = boot ? boot.user_id : null
      if (!boot) sendPageView(ctx)
    } else {
      sendPageView(ctx)
    }

    state.lastLocation = ctx.page_location || state.lastLocation
    identify(ctx)
    consumeEvents()
  })

  // The previous (already sanitised) page_location without its query string, or the
  // server-scrubbed referrer; never the raw document.referrer.
  const pageReferrer = (ctx) => {
    const referrer = String(state.lastLocation || ctx.page_referrer || '').replace(QUERY_OR_HASH, '')
    return referrer || undefined
  }

  const sendPageView = (ctx) => {
    if (!ctx.measurement_id || !ctx.page_location) return
    const referrer = pageReferrer(ctx)
    const shared = { page_title: ctx.page_title, page_location: ctx.page_location }
    if (referrer) shared.page_referrer = referrer
    gtag('set', shared)

    if (ctx.user_id && ctx.user_id !== state.userId) {
      const config = { user_id: ctx.user_id, send_page_view: false }
      if (ctx.traffic_type === 'internal') config.traffic_type = 'internal'
      gtag('config', ctx.measurement_id, config)
      state.userId = ctx.user_id
    }
    if (ctx.user_id) gtag('set', 'user_properties', { plan_type: ctx.plan_type || 'free' })

    const params = { page_title: ctx.page_title, page_location: ctx.page_location }
    if (referrer) params.page_referrer = referrer
    gtag('event', 'page_view', params)
  }

  const start = ({ turbo = true } = {}) => safe(() => {
    const d = doc()
    if (!d || state.started) return
    state.started = true

    if (turbo) {
      d.addEventListener('turbo:load', handlePageLoad)
      d.addEventListener('turbo:frame-load', (event) => consumeEvents(event.target))
    } else if (d.readyState === 'loading') {
      d.addEventListener('DOMContentLoaded', handlePageLoad)
    } else {
      handlePageLoad()
    }
  })

  return { track, trackBeforeNavigate, setTag, identify, consumeEvents, handlePageLoad, readContext, start, state }
}

const analytics = createAnalytics()

export default analytics
